/**
 * Multiple Visualization Paradigms
 * 
 * Provides different visualization modes for analyzing supply chains
 * Requirements: 4A.1-4A.9
 */

import React, { useState, useEffect, useRef } from 'react';
import * as d3 from 'd3';
import { GraphData, GraphNode } from './ForceDirectedGraph';

export type VisualizationMode =
  | 'force-directed'
  | 'data-flow';

interface VisualizationModesProps {
  data: GraphData;
  mode: VisualizationMode;
  onModeChange: (mode: VisualizationMode) => void;
  scenarios?: GraphData[];
  selectedEntityId?: string;
}

/**
 * Visualization Modes Component
 * 
 * Manages switching between different visualization paradigms
 */
export const VisualizationModes: React.FC<VisualizationModesProps> = ({
  data,
  mode,
  onModeChange,
  scenarios,
  selectedEntityId,
}) => {
  const [viewState, _setViewState] = useState({
    selectedNodeId: selectedEntityId || null,
    zoom: 1,
    pan: { x: 0, y: 0 },
  });

  const renderVisualization = () => {
    switch (mode) {
      case 'data-flow':
        return <DataFlowView data={data} viewState={viewState} />;
      
      default:
        return <div>Select a visualization mode</div>;
    }
  };

  return (
    <div className="visualization-modes" style={{ position: 'relative', width: '100%', height: '100%' }}>
      {/* Visualization Container */}
      <div className="visualization-container" style={{ width: '100%', height: '100%' }}>
        {renderVisualization()}
      </div>
    </div>
  );
};

/**
 * Data Flow View (Requirement 4A.5)
 * 
 * Sankey-style diagram showing data flow with:
 * - Reversed arrows (target -> source)
 * - Edge colors based on tension
 * - Node colors based on risk score
 */
const DataFlowView: React.FC<{ data: GraphData; viewState: any }> = ({ data }) => {
  const svgRef = useRef<SVGSVGElement>(null);

  useEffect(() => {
    if (!svgRef.current || !data.nodes.length) return;

    const width = 800;
    const height = 600;

    d3.select(svgRef.current).selectAll('*').remove();

    const svg = d3.select(svgRef.current);
    const g = svg.append('g');

    // Simple left-to-right flow layout
    const layers = 3;
    const nodesPerLayer = Math.ceil(data.nodes.length / layers);
    
    const positionedNodes = data.nodes.map((node, i) => ({
      ...node,
      x: (Math.floor(i / nodesPerLayer) + 1) * (width / (layers + 1)),
      y: ((i % nodesPerLayer) + 1) * (height / (nodesPerLayer + 1)),
    }));

    // Calculate min/max tension and risk for relative coloring
    const tensions = data.edges.map(e => e.maxTension).filter(t => t > 0);
    const minTension = tensions.length > 0 ? Math.min(...tensions) : 0;
    const maxTension = tensions.length > 0 ? Math.max(...tensions) : 0;
    
    const riskScores = data.nodes.map(n => n.riskScore).filter(r => r > 0);
    const minRisk = riskScores.length > 0 ? Math.min(...riskScores) : 0;
    const maxRisk = riskScores.length > 0 ? Math.max(...riskScores) : 0;

    console.log('[FLOW DIAGRAM] Risk scores:', { riskScores, minRisk, maxRisk });
    console.log('[FLOW DIAGRAM] Nodes:', data.nodes.map(n => ({ id: n.id, name: n.name, riskScore: n.riskScore })));

    // Add arrow marker (pointing at target node)
    svg.append('defs').append('marker')
      .attr('id', 'arrow-flow')
      .attr('viewBox', '0 -5 10 10')
      .attr('refX', 10)
      .attr('refY', 0)
      .attr('markerWidth', 6)
      .attr('markerHeight', 6)
      .attr('orient', 'auto') // Auto orientation to point along path
      .append('path')
      .attr('d', 'M0,-5L10,0L0,5') // Standard arrow shape
      .attr('fill', '#666');

    // Draw flow arrows (REVERSED: target -> source)
    data.edges.forEach(edge => {
      const source = positionedNodes.find(n => n.id === (typeof edge.source === 'string' ? edge.source : edge.source.id));
      const target = positionedNodes.find(n => n.id === (typeof edge.target === 'string' ? edge.target : edge.target.id));
      
      if (source && target) {
        // REVERSED: Draw from target to source
        const edgeColor = getRelativeTensionColor(edge.maxTension, minTension, maxTension);
        
        // Calculate path endpoints to stop before reaching the node centers
        // Node rectangles are 80x40, so we need to stop 40px before target and source
        const dx = source.x! - target.x!;
        const dy = source.y! - target.y!;
        const distance = Math.sqrt(dx * dx + dy * dy);
        
        // Offset by 45px (slightly more than half the node width) to show arrows
        const offsetDistance = 45;
        const offsetRatio = offsetDistance / distance;
        
        const targetX = target.x! + dx * offsetRatio;
        const targetY = target.y! + dy * offsetRatio;
        const sourceX = source.x! - dx * offsetRatio;
        const sourceY = source.y! - dy * offsetRatio;
        
        g.append('path')
          .attr('d', `M ${targetX} ${targetY} Q ${(sourceX + targetX) / 2} ${targetY} ${sourceX} ${sourceY}`)
          .attr('fill', 'none')
          .attr('stroke', edgeColor)
          .attr('stroke-width', 3)
          .attr('stroke-opacity', 0.7)
          .attr('marker-end', 'url(#arrow-flow)'); // Arrow at end (pointing at source/target node)
      }
    });

    // Draw nodes (colored by risk score)
    positionedNodes.forEach(node => {
      const nodeGroup = g.append('g')
        .attr('transform', `translate(${node.x},${node.y})`);

      // Get node color based on relative risk score
      const nodeColor = getRelativeRiskColor(node.riskScore, minRisk, maxRisk);
      
      console.log(`[FLOW DIAGRAM] Node ${node.name}: risk=${node.riskScore}, color=${nodeColor}`);

      nodeGroup.append('rect')
        .attr('x', -40)
        .attr('y', -20)
        .attr('width', 80)
        .attr('height', 40)
        .attr('fill', nodeColor)
        .attr('stroke', 'white')
        .attr('stroke-width', 2)
        .attr('rx', 4);

      nodeGroup.append('text')
        .text(node.name)
        .attr('text-anchor', 'middle')
        .attr('dy', 4)
        .attr('font-size', '10px')
        .attr('fill', '#000000')
        .attr('font-weight', '600');
    });

  }, [data]);

  return (
    <div style={{ width: '100%', height: '100%', display: 'flex', justifyContent: 'center', alignItems: 'center' }}>
      <svg ref={svgRef} width={800} height={600} style={{ border: '1px solid #ccc', background: '#fafafa' }} />
      
      {/* Legend */}
      <div style={{
        position: 'absolute',
        bottom: 20,
        right: 20,
        background: 'white',
        border: '1px solid #ccc',
        borderRadius: '8px',
        padding: '12px',
        fontSize: '11px',
        boxShadow: '0 2px 8px rgba(0,0,0,0.1)',
      }}>
        <div style={{ fontWeight: 'bold', marginBottom: '8px' }}>Legend</div>
        <div style={{ marginBottom: '6px' }}>
          <div style={{ fontWeight: '600', marginBottom: '4px' }}>Edge Colors (Tension):</div>
          <div style={{ display: 'flex', alignItems: 'center', gap: '4px', marginBottom: '2px' }}>
            <div style={{ width: '12px', height: '12px', background: '#22c55e', borderRadius: '2px' }}></div>
            <span>Low</span>
          </div>
          <div style={{ display: 'flex', alignItems: 'center', gap: '4px', marginBottom: '2px' }}>
            <div style={{ width: '12px', height: '12px', background: '#eab308', borderRadius: '2px' }}></div>
            <span>Medium</span>
          </div>
          <div style={{ display: 'flex', alignItems: 'center', gap: '4px', marginBottom: '2px' }}>
            <div style={{ width: '12px', height: '12px', background: '#ef4444', borderRadius: '2px' }}></div>
            <span>High</span>
          </div>
        </div>
        <div style={{ marginTop: '8px', paddingTop: '8px', borderTop: '1px solid #e5e7eb' }}>
          <div style={{ fontWeight: '600', marginBottom: '4px' }}>Node Colors (Risk):</div>
          <div style={{ display: 'flex', alignItems: 'center', gap: '4px', marginBottom: '2px' }}>
            <div style={{ width: '12px', height: '12px', background: '#3b82f6', borderRadius: '2px' }}></div>
            <span>Low Risk</span>
          </div>
          <div style={{ display: 'flex', alignItems: 'center', gap: '4px', marginBottom: '2px' }}>
            <div style={{ width: '12px', height: '12px', background: '#eab308', borderRadius: '2px' }}></div>
            <span>Medium Risk</span>
          </div>
          <div style={{ display: 'flex', alignItems: 'center', gap: '4px', marginBottom: '2px' }}>
            <div style={{ width: '12px', height: '12px', background: '#dc2626', borderRadius: '2px' }}></div>
            <span>High Risk</span>
          </div>
        </div>
      </div>
    </div>
  );
};

/**
 * Map tension to color using relative scale (green -> yellow -> red)
 * Based on min/max tension in the current graph
 */
function getRelativeTensionColor(tension: number, minTension: number, maxTension: number): string {
  // Handle edge cases
  if (maxTension === minTension) return '#eab308'; // Yellow if all same
  if (tension <= 0) return '#22c55e'; // Green for zero/negative
  
  // Normalize tension to 0-1 range
  const normalized = (tension - minTension) / (maxTension - minTension);
  
  // Map to color spectrum: green (0) -> yellow (0.5) -> red (1)
  if (normalized < 0.5) {
    // Green to Yellow
    const t = normalized * 2; // 0 to 1
    return interpolateColor('#22c55e', '#eab308', t);
  } else {
    // Yellow to Red
    const t = (normalized - 0.5) * 2; // 0 to 1
    return interpolateColor('#eab308', '#ef4444', t);
  }
}

/**
 * Map risk score to color using relative scale (blue -> yellow -> red)
 * Based on min/max risk in the current graph
 */
function getRelativeRiskColor(riskScore: number, minRisk: number, maxRisk: number): string {
  // Handle edge cases
  if (maxRisk === minRisk) return '#eab308'; // Yellow if all same
  if (riskScore <= 0) return '#3b82f6'; // Blue for zero/negative
  
  // Normalize risk to 0-1 range
  const normalized = (riskScore - minRisk) / (maxRisk - minRisk);
  
  // Map to color spectrum: blue (0) -> yellow (0.5) -> red (1)
  if (normalized < 0.5) {
    // Blue to Yellow
    const t = normalized * 2; // 0 to 1
    return interpolateColor('#3b82f6', '#eab308', t);
  } else {
    // Yellow to Red
    const t = (normalized - 0.5) * 2; // 0 to 1
    return interpolateColor('#eab308', '#dc2626', t);
  }
}

/**
 * Interpolate between two hex colors
 */
function interpolateColor(color1: string, color2: string, t: number): string {
  const r1 = parseInt(color1.slice(1, 3), 16);
  const g1 = parseInt(color1.slice(3, 5), 16);
  const b1 = parseInt(color1.slice(5, 7), 16);
  
  const r2 = parseInt(color2.slice(1, 3), 16);
  const g2 = parseInt(color2.slice(3, 5), 16);
  const b2 = parseInt(color2.slice(5, 7), 16);
  
  const r = Math.round(r1 + (r2 - r1) * t);
  const g = Math.round(g1 + (g2 - g1) * t);
  const b = Math.round(b1 + (b2 - b1) * t);
  
  return `#${r.toString(16).padStart(2, '0')}${g.toString(16).padStart(2, '0')}${b.toString(16).padStart(2, '0')}`;
}
